import { MultiSelect, MultiSelectProps, Select, SelectProps, rem } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { nanoid } from 'nanoid';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { TABLET_BREAKPOINT } from 'theme/components/utils/tablet';
import P2Regular from 'components/typography/P2Regular';

/**
 * The base interface for the DataSelectProps type.
 */
interface IDataSelectProps<TRow> {
  action: () => Promise<TRow[]>;
  onReady?: (data: TRow[]) => void;
  valueProp: keyof TRow;
  labelProp: keyof TRow;
  imageProp?: keyof TRow;
  groupProp?: keyof TRow;
  filterData?: (data: TRow[]) => TRow[];
  loadingPlaceholder?: string;
  lazyLoad?: boolean;
}

/**
 * The parameters for the DataSelect component.
 */
type DataSelectProps<TRow> = IDataSelectProps<TRow> &
  Omit<SelectProps, 'data' | 'multiple'> & {
    autoSelectSingleResult?: boolean;
  };

/**
 * The parameters for the DataMultiSelect component.
 */
type DataMultiSelectProps<TRow> = IDataSelectProps<TRow> &
  Omit<MultiSelectProps, 'data' | 'multiple'> & {
    compact?: boolean;
  };

type MultiSelectStyles = NonNullable<MultiSelectProps['styles']>;

interface CompactDropdownItemProps extends React.ComponentPropsWithoutRef<'div'> {
  itemComponent?: React.ComponentType<any>;
  label?: string;
  value: string;
  selected?: boolean;
}

interface CompactSummaryValueProps extends React.ComponentPropsWithoutRef<'div'> {
  index?: number;
}

const COMPACT_INPUT_HEIGHTS = {
  lg: { desktop: 48, mobile: 40 },
  md: { desktop: 40, mobile: 32 },
  sm: { desktop: 32, mobile: 28 },
} as const;

const DEFAULT_COMPACT_HEIGHTS = COMPACT_INPUT_HEIGHTS.md;

/**
 * Guards style fragments before shallow-merging them.
 */
const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Normalizes Mantine style input so object and callback forms can be merged uniformly.
 */
export const resolveStyles = (styles: MultiSelectStyles | undefined, theme: any, params: any): Record<string, any> => {
  if (!styles) {
    return {};
  }

  return typeof styles === 'function' ? (styles as any)(theme, params) ?? {} : styles;
};

/**
 * Shallow-merges top-level Mantine style keys while preserving nested declarations per key.
 */
const mergeStyleRecords = (...records: Array<Record<string, any>>): Record<string, any> => {
  const merged: Record<string, any> = {};

  records.forEach((record) => {
    Object.entries(record).forEach(([key, value]) => {
      if (isPlainObject(merged[key]) && isPlainObject(value)) {
        merged[key] = { ...merged[key], ...value };
        return;
      }

      merged[key] = value;
    });
  });

  return merged;
};

/**
 * Combines wrapper-provided compact styles with caller overrides.
 */
const mergeMultiSelectStyles = (
  baseStyles: MultiSelectStyles | undefined,
  overrideStyles: MultiSelectStyles | undefined
): MultiSelectStyles | undefined => {
  if (!baseStyles) {
    return overrideStyles;
  }

  if (!overrideStyles) {
    return baseStyles;
  }

  return ((theme: any, params: any) =>
    mergeStyleRecords(resolveStyles(baseStyles, theme, params), resolveStyles(overrideStyles, theme, params))) as any;
};

/**
 * Maps compact mode to the fixed input heights used by the matching Select sizes.
 */
const getCompactHeights = (size: MultiSelectProps['size']) => {
  if (typeof size === 'string' && size in COMPACT_INPUT_HEIGHTS) {
    return COMPACT_INPUT_HEIGHTS[size as keyof typeof COMPACT_INPUT_HEIGHTS];
  }

  return DEFAULT_COMPACT_HEIGHTS;
};

/**
 * Builds compact-only MultiSelect style overrides without affecting the global theme.
 */
const createCompactStyles = (size: MultiSelectProps['size']): MultiSelectStyles => {
  const heights = getCompactHeights(size);

  return (theme: any) => ({
    wrapper: {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      contain: 'inline-size',
    },
    input: {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      contain: 'inline-size',
      height: `${rem(heights.desktop)} !important`,
      minHeight: `${rem(heights.desktop)} !important`,
      maxHeight: `${rem(heights.desktop)} !important`,
      overflow: 'hidden',
      [theme.fn.smallerThan(TABLET_BREAKPOINT)]: {
        height: `${rem(heights.mobile)} !important`,
        minHeight: `${rem(heights.mobile)} !important`,
        maxHeight: `${rem(heights.mobile)} !important`,
      },
    },
    values: {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      contain: 'inline-size',
      height: '100%',
      minHeight: 0,
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      marginLeft: 0,
      gap: rem(6),
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      '&[data-clearable]': {
        marginRight: rem(28),
      },
    },
    item: {
      '&[data-selected]': {
        backgroundColor: theme.colors.red[0],
        color: theme.colors.red[9],
      },
      '&[data-selected]:hover': {
        backgroundColor: theme.colors.red[1],
        color: theme.colors.red[9],
      },
    },
    value: {
      flex: '0 0 auto',
      alignSelf: 'center',
      margin: 0,
      maxWidth: 'none',
      whiteSpace: 'nowrap',
    },
    searchInput: {
      flex: '0 0 auto',
      minWidth: rem(24),
      width: rem(24),
      maxHeight: 'none',
      alignSelf: 'center',
      marginLeft: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
    searchInputEmpty: {
      flex: '1 1 auto',
      width: '100%',
    },
  });
};

/**
 * Wraps the dropdown item renderer to append a trailing check icon in compact mode.
 */
const createCompactItemComponent = (itemComponent?: React.ComponentType<any>) => {
  const CompactDropdownItem = forwardRef<HTMLDivElement, CompactDropdownItemProps>(function CompactDropdownItem(
    { itemComponent: _, selected = false, className, style, onMouseEnter, onMouseDown, id, role, tabIndex, ...props },
    ref
  ) {
    const {
      item,
      label,
      value,
      disabled,
      variant,
      'data-hovered': dataHovered,
      'data-selected': dataSelected,
      ...contentProps
    } = props as Record<string, any>;
    const ItemComponent = itemComponent;

    return (
      <div
        ref={ref}
        className={className}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseDown={onMouseDown}
        id={id}
        role={role}
        tabIndex={tabIndex}
        data-disabled={disabled || undefined}
        data-hovered={dataHovered || undefined}
        data-selected={dataSelected || undefined}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: rem(8), minWidth: 0 }}>
          <IconCheck aria-hidden="true" size={16} stroke={1.75} style={{ opacity: selected ? 1 : 0, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            {ItemComponent ? (
              <ItemComponent
                {...contentProps}
                item={item}
                label={label}
                value={value}
                selected={selected}
                disabled={disabled}
                variant={variant}
              />
            ) : (
              label || value
            )}
          </div>
        </div>
      </div>
    );
  });

  CompactDropdownItem.displayName = 'CompactDropdownItem';

  return CompactDropdownItem;
};

/**
 * Renders a single compact summary instead of per-item badges.
 */
const createCompactValueComponent = (summaryLabel: string | null) => {
  const CompactSummaryValue = forwardRef<HTMLDivElement, CompactSummaryValueProps>(function CompactSummaryValue(
    { index = 0, className, ...props },
    ref
  ) {
    if (index > 0 || !summaryLabel) {
      return null;
    }

    return (
      <div ref={ref} className={className} {...props}>
        <P2Regular color="gray.7">{summaryLabel}</P2Regular>
      </div>
    );
  });

  CompactSummaryValue.displayName = 'CompactSummaryValue';

  return CompactSummaryValue;
};

/**
 * The inherited parameters for the DataSelect component.
 */
export type DataSelectInheritedProps = Omit<
  DataSelectProps<any>,
  'action' | 'valueProp' | 'labelProp' | 'imageProp' | 'groupProp'
>;

/**
 * The inherited parameters for the DataMultiSelect component.
 */
export type DataMultiSelectInheritedProps = Omit<
  DataMultiSelectProps<any>,
  'action' | 'valueProp' | 'labelProp' | 'imageProp' | 'groupProp'
>;

/**
 * Computes value and label pairs from the data.
 *
 * The value should always be string, otherwise the Select will behave unexpectedly
 */
function computePairs<TRow>(
  data: TRow[],
  valueProp: keyof TRow,
  labelProp: keyof TRow,
  imageProp?: keyof TRow,
  groupProp?: keyof TRow
) {
  return data.map((item: TRow) => {
    const res: { item: TRow; value: string; label: string; image?: string; group?: string } = {
      item,
      value: String(item[valueProp]),
      label: String(item[labelProp]),
    };

    if (imageProp) {
      res.image = String(item[imageProp]);
    }

    if (groupProp) {
      res.group = String(item[groupProp]);
    }

    return res;
  });
}

/**
 * Normalizes empty select sentinels used across table filters to a missing value.
 */
const normalizeSelectValue = (value: unknown): string | null => {
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    value === -1 ||
    value === '-1' ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return null;
  }

  return String(value);
};

/**
 * Default option-list transformation.
 */
function identityData<TRow>(data: TRow[]): TRow[] {
  return data;
}

/**
 * Used to fetch objects from the database and display them in a select.
 */
export function DataSelect<TRow>({
  action,
  onReady = noop,
  valueProp,
  labelProp,
  placeholder,
  imageProp = undefined,
  groupProp = undefined,
  filterData = identityData,
  loadingPlaceholder = 'Načítavanie ...',
  autoSelectSingleResult = false,
  lazyLoad = false,
  value,
  onChange,
  itemComponent,
  ...rest
}: DataSelectProps<TRow>) {
  const normalizedValue = normalizeSelectValue(value);
  const fetchDataOnMount = normalizedValue !== null || !lazyLoad;

  const [data, setData] = useState<TRow[] | null>(null);
  const [loading, setLoading] = useState(fetchDataOnMount);
  const filteredData = useMemo(() => filterData(data ?? []), [data, filterData]);

  const valueLabelPairs = useMemo(() => {
    if (loading && !autoSelectSingleResult) {
      return [{ value: nanoid(), label: 'Načítavanie ...' }];
    }

    return computePairs(filteredData, valueProp, labelProp, imageProp, groupProp);
  }, [filteredData, loading, valueProp, labelProp, imageProp, groupProp]);

  /**
   * Fetches the data from the server.
   */
  const fetchData = useCallback(() => {
    action()
      .then((data) => {
        setData(data);
        onReady(data);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  }, [action, setData, setLoading]);

  /**
   * Fetches the data from the server if lazyLoad is true and the data is not
   * loaded yet.
   */
  const maybeLazyLoad = useCallback(() => {
    if (lazyLoad && !loading && data === null) {
      setLoading(true);
      fetchData();
    }
  }, [lazyLoad, loading, data, fetchData]);

  // Apply autoSelectSingleResult when the data is loaded.
  useEffect(() => {
    if (autoSelectSingleResult && valueLabelPairs.length === 1 && normalizedValue === null) {
      onChange?.(valueLabelPairs[0].value);
    }
  }, [autoSelectSingleResult, normalizedValue, valueLabelPairs]);

  // Apply auto select in individual items.
  useEffect(() => {
    if (normalizedValue === null) {
      const autoSelect = data?.find((item) => (item as any).autoSelect);

      if (autoSelect) {
        onChange?.(String((autoSelect as any)[valueProp]));
      }
    }
  }, [data, normalizedValue]);

  // Fetch data when lazy-loaded selected value arrives after mount so its
  // label can be resolved without opening the dropdown first.
  useEffect(() => {
    if (lazyLoad && !loading && data === null && normalizedValue !== null) {
      setLoading(true);
      fetchData();
    }
  }, [lazyLoad, loading, data, normalizedValue, fetchData]);

  // Fetch data when the component is mounted if lazyLoad is false.
  useEffect(() => {
    if (fetchDataOnMount) {
      fetchData();
    }
  }, []);

  return (
    <Select
      {...rest}
      itemComponent={loading ? undefined : itemComponent}
      value={normalizedValue}
      onChange={onChange}
      placeholder={loading && !lazyLoad ? loadingPlaceholder : placeholder}
      data={valueLabelPairs}
      searchable
      nothingFound="Žiadne výsledky"
      onDropdownOpen={maybeLazyLoad}
    />
  );
}

/**
 * Used to fetch objects from the database and display them in a select (multiple).
 */
export function DataMultiSelect<TRow>({
  action,
  onReady = noop,
  valueProp,
  labelProp,
  placeholder,
  imageProp = undefined,
  groupProp = undefined,
  filterData = identityData,
  loadingPlaceholder = 'Načítavanie ...',
  lazyLoad = false,
  compact = false,
  itemComponent,
  value,
  defaultValue,
  onChange,
  clearable,
  disableSelectedItemFiltering,
  dropdownComponent,
  wrapperProps,
  styles,
  size = 'md',
  ...rest
}: DataMultiSelectProps<TRow>) {
  const hasInitialValue = Array.isArray(value)
    ? value.length > 0
    : Array.isArray(defaultValue) && defaultValue.length > 0;
  const fetchDataOnMount = hasInitialValue || !lazyLoad;
  const [data, setData] = useState<TRow[] | null>(null);
  const [loading, setLoading] = useState(fetchDataOnMount);
  const filteredData = useMemo(() => filterData(data ?? []), [data, filterData]);
  const [compactValue, setCompactValue] = useState<string[]>(() => {
    if (Array.isArray(value)) {
      return value.map(String);
    }

    if (Array.isArray(defaultValue)) {
      return defaultValue.map(String);
    }

    return [];
  });
  /**
   * Fetches the data from the server.
   */
  const fetchData = useCallback(() => {
    action()
      .then((nextData) => {
        setData(nextData);
        onReady(nextData);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  }, [action, onReady]);

  /**
   * Fetches the data from the server if lazyLoad is true and the data is not
   * loaded yet.
   */
  const maybeLazyLoad = useCallback(() => {
    if (lazyLoad && !loading && data === null) {
      setLoading(true);
      fetchData();
    }
  }, [lazyLoad, loading, data, fetchData]);

  const valueLabelPairs = useMemo(
    () => computePairs(filteredData, valueProp, labelProp, imageProp, groupProp),
    [filteredData, valueProp, labelProp, imageProp, groupProp]
  );
  const mergedStyles = useMemo(
    () => mergeMultiSelectStyles(compact ? createCompactStyles(size) : undefined, styles),
    [compact, size, styles]
  );
  const resolvedItemComponent = useMemo(
    () => (compact ? createCompactItemComponent(itemComponent) : itemComponent),
    [compact, itemComponent]
  );
  const compactSummaryLabel = useMemo(() => {
    if (compactValue.length === 0) {
      return null;
    }

    if (compactValue.length === 1) {
      return valueLabelPairs.find((item) => item.value === compactValue[0])?.label ?? compactValue[0];
    }

    return `Vybrané: ${compactValue.length}`;
  }, [compactValue, valueLabelPairs]);
  const resolvedValueComponent = useMemo(
    () => (compact ? createCompactValueComponent(compactSummaryLabel) : undefined),
    [compact, compactSummaryLabel]
  );
  const resolvedDropdownComponent = compact ? dropdownComponent ?? 'div' : dropdownComponent;
  const resolvedWrapperProps = useMemo(() => {
    if (!compact) {
      return wrapperProps;
    }

    return {
      ...wrapperProps,
      style: {
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        contain: 'inline-size',
        ...(wrapperProps?.style ?? {}),
      },
    };
  }, [compact, wrapperProps]);

  useEffect(() => {
    if (Array.isArray(value)) {
      setCompactValue(value.map(String));
    }
  }, [value]);

  // Fetch data when lazy-loaded selected values arrive after mount so their
  // labels can be resolved without opening the dropdown first.
  useEffect(() => {
    if (lazyLoad && !loading && data === null && compactValue.length > 0) {
      setLoading(true);
      fetchData();
    }
  }, [lazyLoad, loading, data, compactValue, fetchData]);

  // Fetch data when the component is mounted if lazyLoad is false, or when
  // selected values need labels resolved immediately.
  useEffect(() => {
    if (fetchDataOnMount) {
      fetchData();
    }
  }, []);

  /**
   * Keeps compact summary display synchronized for controlled and uncontrolled usage.
   */
  const handleChange = useCallback(
    (nextValue: string[]) => {
      setCompactValue(nextValue.map(String));
      onChange?.(nextValue);
    },
    [onChange]
  );

  return (
    <MultiSelect
      {...rest}
      size={size}
      value={value}
      defaultValue={defaultValue}
      onChange={handleChange}
      placeholder={loading && !lazyLoad ? loadingPlaceholder : placeholder}
      data={valueLabelPairs}
      itemComponent={resolvedItemComponent}
      valueComponent={resolvedValueComponent}
      dropdownComponent={resolvedDropdownComponent}
      clearable={clearable}
      wrapperProps={resolvedWrapperProps}
      disableSelectedItemFiltering={compact || disableSelectedItemFiltering}
      styles={mergedStyles}
      searchable
      nothingFound="Žiadne výsledky"
      onDropdownOpen={maybeLazyLoad}
    />
  );
}
